import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import ConstData from "../../lib/ConstData";

import { get_url_meta } from "../../lib/api/admin";
import Alerts from "../etc/list/Alerts";

// 260731 URL 등록/편집에서 공통으로 사용할 HTTP(S) URL 정규화 및 검증
const normalizeAndValidateHttpUrl = (value) => {
    const trimmed = String(value ?? "").trim();

    if (!trimmed) {
        return { isValid: false, url: "", errorKey: "URL_입력_필수" };
    }

    const hasScheme = /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed);
    const normalized = hasScheme ? trimmed : `https://${trimmed}`;

    try {
        const parsed = new URL(normalized);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
            return { isValid: false, url: "", errorKey: "URL_프로토콜_오류" };
        }
        if (!parsed.hostname || !parsed.hostname.includes(".")) {
            return { isValid: false, url: "", errorKey: "URL_형식_오류" };
        }
        return { isValid: true, url: parsed.href, errorKey: "" };
    } catch (err) {
        return { isValid: false, url: "", errorKey: "URL_형식_오류" };
    }
};

// ============================================================
// SVG 아이콘 컴포넌트
// ============================================================

const IconTeacher = () => (
    <svg className="icon-teacher" xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 21a8 8 0 0 1 13.292-6" />
        <circle cx="10" cy="8" r="5" />
        <path d="M19 16v6" />
        <path d="M22 19h-6" />
    </svg>
);

const IconSchool = () => (
    <svg className="icon-school" xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 21v-3a2 2 0 0 0-4 0v3" />
        <path d="M18 4.933V21" />
        <path d="m4 6 7.106-3.79a2 2 0 0 1 1.788 0L20 6" />
        <path d="m6 11-3.52 2.147a1 1 0 0 0-.48.854V19a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a1 1 0 0 0-.48-.853L18 11" />
        <path d="M6 4.933V21" />
        <circle cx="12" cy="9" r="2" />
    </svg>
);

const IconClear = ({ onClick }) => (
    <svg className="icon-clear" onClick={onClick}
        xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const IconPlus = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

const IconMinus = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

const IconSave = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
        <polyline points="17 21 17 13 7 13 7 21" />
        <polyline points="7 3 7 8 15 8" />
    </svg>
);

const IconDelete = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <line x1="10" y1="11" x2="10" y2="17" />
        <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
);

const IconPencil = ({ onClick }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
        viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        className="lucide-pencil-line-icon"
        onClick={onClick}
        style={{ cursor: 'pointer', marginLeft: 5, verticalAlign: 'middle', color: '#666' }}>
        <path d="M13 21h8" />
        <path d="m15 5 4 4" />
        <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
    </svg>
);

// 고정 URL 탭 타이틀 아이콘 + 파비콘 로드 실패 시 대체 아이콘으로 공용 사용
// → className을 외부에서 주입받아 사용처별로 크기/색상을 다르게 적용
const IconLink = ({ className = "" }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 17H7A5 5 0 0 1 7 7h2" />
        <path d="M15 7h2a5 5 0 1 1 0 10h-2" />
        <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
);

// 사이트주소 셀에서 "새 탭으로 열기" 버튼용 아이콘
const IconExternalLink = ({ onClick }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        onClick={onClick}
        style={{ cursor: 'pointer', marginLeft: 4, verticalAlign: 'middle', color: '#3b63b2', flexShrink: 0 }}>
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
);

const IconFirst = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 17 6 12 11 7" /><polyline points="18 17 13 12 18 7" /></svg>);
const IconPrev = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>);
const IconNext = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>);
const IconLast = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="13 17 18 12 13 7" /><polyline points="6 17 11 12 6 7" /></svg>);

// ============================================================
// 상수
// ============================================================
const DEFAULT_ROWS_PER_PAGE = 10; // 페이지당 표시 개수 기본값
const ROWS_PER_PAGE_OPTIONS = [10, 20, 30, 50, 100]; // 선택 가능한 페이지당 표시 개수

// ============================================================
// InlineEditCell
// 이름 셀 클릭 시 입력창으로 전환, 확인/취소 처리
// ============================================================
const InlineEditCell = ({ item, nameKey, onSave }) => {
    const { t } = useTranslation();

    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(item[nameKey]);

    const handleStartEdit = () => {
        setEditValue(item[nameKey]);
        setIsEditing(true);
    };

    const handleConfirm = () => {
        const trimmed = editValue.trim();
        if (!trimmed) {
            alert(nameKey === "teacherName" ? t("선생님_이름_입력", { ns: "admin" }) : t("학급_이름_입력", { ns: "admin" }));
            return;
        }
        onSave(item.id, trimmed);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditValue(item[nameKey]);
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <div className="inline-edit-cell">
                {/* 20260826 선생님/클래스 이름 편집 입력창을 셀 폭에 맞춰 넓게 배치한다. */}
                <input
                    type="text"
                    className="custom-input inline-edit-input"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    autoFocus
                />
                <button className="btn-table-action inline-edit-button" onClick={handleConfirm}>확인</button>
                <button className="btn-table-action" onClick={handleCancel}>취소</button>
            </div>
        );
    }

    return (
        <>
            <span className={`${nameKey === "teacherName" ? "teacher-name-text" : "class-name-text"}${item.isActive ? " text-active" : ""}`}>
                {item[nameKey]}
            </span>
            <IconPencil onClick={handleStartEdit} />
        </>
    );
};

// ============================================================
// ToggleSwitch
// ============================================================
const ToggleSwitch = ({ checked, onChange }) => (
    <label className="toggle-switch">
        <input type="checkbox" checked={checked} onChange={onChange} />
        <span className="slider" />
    </label>
);

// ============================================================
// Pagination
// ============================================================
const Pagination = ({ total, currentPage, onChangePage, rowsPerPage, onChangeRowsPerPage }) => {
    const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

    return (
        <div className="pagination-container">
            <button className="page-arrow" onClick={() => onChangePage(1)} aria-label="First"><IconFirst /></button>
            <button className="page-arrow" onClick={() => onChangePage(Math.max(1, currentPage - 1))} aria-label="Prev"><IconPrev /></button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                    key={page}
                    className={`page-number${page === currentPage ? " active" : ""}`}
                    onClick={() => onChangePage(page)}
                >
                    {page}
                </button>
            ))}
            <button className="page-arrow" onClick={() => onChangePage(Math.min(totalPages, currentPage + 1))} aria-label="Next"><IconNext /></button>
            <button className="page-arrow" onClick={() => onChangePage(totalPages)} aria-label="Last"><IconLast /></button>
            {
                // 페이지당 표시 개수 선택 (teacher/class/url 3개 탭 공용)
                typeof onChangeRowsPerPage === "function" &&
                <select
                    className="custom-select small pagination-rows-select"
                    value={rowsPerPage}
                    onChange={(e) => onChangeRowsPerPage(Number(e.target.value))}
                >
                    {ROWS_PER_PAGE_OPTIONS.map(n => (
                        <option key={"rows-" + n} value={n}>{n}개씩</option>
                    ))}
                </select>
            }
        </div>
    );
};

// ============================================================
// RegistrationForm
// 선생님/학급 이름 동적 입력창
// ============================================================
const RegistrationForm = ({ isVisible, onSubmit, onClose, mode }) => {
    const { t } = useTranslation();

    const [inputRows, setInputRows] = useState([{ id: Date.now(), value: "" }]);

    const addRow = () => {
        setInputRows(prev => [...prev, { id: Date.now(), value: "" }]);
    };

    const removeRow = (id) => {
        setInputRows(prev => prev.filter(r => r.id !== id));
    };

    const handleChange = (id, value) => {
        setInputRows(prev => prev.map(r => r.id === id ? { ...r, value } : r));
    };

    const clearInput = (id) => {
        setInputRows(prev => prev.map(r => r.id === id ? { ...r, value: "" } : r));
    };

    const handleSubmit = () => {
        const names = inputRows.map(r => r.value.trim()).filter(Boolean);
        if (names.length === 0) {
            alert(mode === "teacher" ? t("선생님_이름_입력", { ns: "admin" }) : t("학급_이름_입력", { ns: "admin" }));
            return;
        }
        onSubmit(names);
        setInputRows([{ id: Date.now(), value: "" }]);
    };

    const handleClose = () => {
        setInputRows([{ id: Date.now(), value: "" }]);
        onClose();
    };

    const placeholder = mode === "teacher" ? t("선생님_이름_힌트", { ns: "admin" }) : t("학급_이름_힌트", { ns: "admin" });
    const title = mode === "teacher" ? t("선생님_추가", { ns: "admin" }) : t("학급_추가", { ns: "admin" });

    return (
        <div className={`form-border-box${isVisible ? "" : " hidden"}`}>
            <div className="form-row font-bold-title border-bottom-light">{title}</div>
            <div className="form-row align-top">
                <div style={{ flex: 1 }}>
                    {inputRows.map((row, idx) => (
                        <div className="dynamic-input-row" key={row.id}>
                            <div className="input-wrapper">
                                <input
                                    type="text"
                                    className="custom-input"
                                    placeholder={placeholder}
                                    value={row.value}
                                    onChange={(e) => handleChange(row.id, e.target.value)}
                                />
                                <IconClear onClick={() => clearInput(row.id)} />
                            </div>
                            {idx === 0 ? (
                                <button className="btn-icon-action btn-add" onClick={addRow} title={t("추가", { ns: "common" })}>
                                    <IconPlus />
                                </button>
                            ) : (
                                <button className="btn-icon-action btn-remove" onClick={() => removeRow(row.id)} title={t("삭제", { ns: "common" })}>
                                    <IconMinus />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <div className="form-submit-row">
                <button className="btn-blue-submit" onClick={handleSubmit}>{t("추가", { ns: "common" })}</button>
            </div>
        </div>
    );
};

// ============================================================
// UrlRegistrationForm
// 고정 URL(CONTENT_URL_INFO) 등록 폼
// - RegistrationForm과 동일한 "동적 행 추가/삭제 + 셔터형 펼침" 구조
// - 단, 한 행에 사이트명/사이트주소/파비콘URL 3개의 입력 필드를 가짐
//   · SITE_NAME, SITE_ADD : NOT NULL → 필수 입력
//   · FAVICON_URL         : NULL 허용 → 비워둔 채 등록 가능
// ============================================================
const UrlRegistrationForm = ({ isVisible, onSubmit, onClose }) => {
    const { t } = useTranslation();

    // 한 행 = { id, siteName, siteAddress, faviconUrl }
    const [inputRows, setInputRows] = useState([
        { id: Date.now(), siteName: "", siteAddress: "", faviconUrl: "" }
    ]);

    // 행별로 메타 fetch 중 여부를 추적 (로딩 스피너 표시용)
    // { [rowId]: boolean }
    const [fetchingMeta, setFetchingMeta] = useState({});

    // 260731 행별 URL 검증/메타조회 오류를 Alerts로 표시
    const [urlErrors, setUrlErrors] = useState({});
    const metaRequestSeq = useRef({});

    useEffect(() => {
        if (!isVisible) {
            setUrlErrors({});
            metaRequestSeq.current = {};
        }
    }, [isVisible]);

    const clearUrlError = (id) => {
        setUrlErrors(prev => {
            if (!prev[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const invalidateMetaRequest = (id) => {
        metaRequestSeq.current[id] = (metaRequestSeq.current[id] || 0) + 1;
    };

    // 행 추가: 새로운 빈 행을 맨 아래에 push
    const addRow = () => {
        setInputRows(prev => [
            ...prev,
            { id: Date.now(), siteName: "", siteAddress: "", faviconUrl: "" }
        ]);
    };

    // 행 삭제 (첫 번째 행은 UI상 항상 "추가(+)" 버튼만 노출되므로 호출되지 않음)
    const removeRow = (id) => {
        invalidateMetaRequest(id);
        setInputRows(prev => prev.filter(r => r.id !== id));
        clearUrlError(id);
    };

    // field: "siteName" | "siteAddress" | "faviconUrl" - 해당 행의 특정 필드값만 변경
    const handleChange = (id, field, value) => {
        setInputRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
        if (field === "siteAddress") {
            invalidateMetaRequest(id);
            clearUrlError(id);
        }
    };

    // X(지우기) 아이콘 클릭 시 해당 필드만 비움
    const clearField = (id, field) => {
        setInputRows(prev => prev.map(r => r.id === id ? { ...r, [field]: "" } : r));
        if (field === "siteAddress") {
            invalidateMetaRequest(id);
            clearUrlError(id);
        }
    };

    // ---------------------------------------------------------------
    // fetchUrlMeta
    // 사이트주소 입력 완료(blur / paste) 시 호출
    // GET /content/url-meta?url=:siteAddress
    // → title이 비어있으면 siteName 자동 채움
    // → faviconUrl이 비어있으면 faviconUrl 자동 채움
    // 이미 사용자가 직접 입력한 값은 덮어쓰지 않음
    // ---------------------------------------------------------------
    const fetchUrlMeta = async (rowId, siteAddress) => {
        // 260731 메타조회 전 공통 함수로 URL을 정규화하고 검증
        const validation = normalizeAndValidateHttpUrl(siteAddress);
        if (!validation.isValid) {
            setUrlErrors(prev => ({ ...prev, [rowId]: validation.errorKey }));
            return;
        }
        const addr = validation.url;
        const requestSeq = (metaRequestSeq.current[rowId] || 0) + 1;
        metaRequestSeq.current[rowId] = requestSeq;

        clearUrlError(rowId);
        setFetchingMeta(prev => ({ ...prev, [rowId]: true }));

        try {
            const res = await get_url_meta({ url: addr });
            const data = res.data;
            if (metaRequestSeq.current[rowId] !== requestSeq) return;
            console.log(data);
            if (!data.isSuccessed) {
                setUrlErrors(prev => ({ ...prev, [rowId]: "URL_메타조회_실패" }));
                return;
            }

            // 현재 행의 최신 값을 읽어서, 비어있는 필드만 채움
            setInputRows(prev => prev.map(r => {
                if (r.id !== rowId) return r;
                return {
                    ...r,
                    // siteName이 비어있을 때만 title로 채움
                    siteName   : r.siteName.trim()   || (data.title      ?? ''),
                    // faviconUrl이 비어있을 때만 채움
                    faviconUrl : r.faviconUrl.trim() || (data.faviconUrl ?? '')
                };
            }));
            clearUrlError(rowId);
        } catch (err) {
            console.log('fetchUrlMeta err =>', err);
            if (metaRequestSeq.current[rowId] === requestSeq) {
                setUrlErrors(prev => ({ ...prev, [rowId]: "URL_메타조회_실패" }));
            }
        } finally {
            if (metaRequestSeq.current[rowId] === requestSeq) {
                setFetchingMeta(prev => ({ ...prev, [rowId]: false }));
            }
        }
    };

    // ---------------------------------------------------------------
    // onBlur: 포커스가 떠날 때 메타 fetch
    // onPaste: 붙여넣기 직후 메타 fetch
    //   - paste 이벤트 시점에는 e.target.value가 아직 갱신 전이므로
    //     e.clipboardData.getData("text")로 붙여넣을 값을 직접 읽음
    // ---------------------------------------------------------------
    const handleAddressBlur = (rowId, currentValue) => {
        if (!currentValue.trim()) return;           // 빈 값이면 무시
        if (fetchingMeta[rowId]) return;            // 이미 fetch 중이면 무시
        fetchUrlMeta(rowId, currentValue);
    };

    const handleAddressPaste = (rowId, e) => {
        const pasted = e.clipboardData.getData('text');
        invalidateMetaRequest(rowId);
        clearUrlError(rowId);
        // paste 이벤트 → onChange보다 먼저 발생하므로 state 반영 전에 직접 넘김
        // setTimeout으로 onChange가 state를 업데이트한 뒤 fetch 시작
        setTimeout(() => fetchUrlMeta(rowId, pasted), 0);
    };

    const handleSubmit = () => {
        // 260731 제출 시에도 모든 URL을 최종 검증하고 정규화
        const nextErrors = {};
        inputRows.forEach(row => {
            if (row.siteName.trim() || row.siteAddress.trim()) {
                const validation = normalizeAndValidateHttpUrl(row.siteAddress);
                if (!validation.isValid) nextErrors[row.id] = validation.errorKey;
            }
        });
        if (Object.keys(nextErrors).length > 0) {
            setUrlErrors(nextErrors);
            return;
        }

        // 1) 각 행의 입력값을 trim 처리
        // 2) 사이트명/사이트주소가 둘 다 빈 행(=완전히 비어있는 행)은 제출 대상에서 제외
        const rows = inputRows
            .map(r => ({
                siteName: r.siteName.trim(),
                siteAddress: normalizeAndValidateHttpUrl(r.siteAddress).url,
                faviconUrl: normalizeAndValidateHttpUrl(r.siteAddress).url,
                // faviconUrl: r.faviconUrl.trim()
            }))
            .filter(r => r.siteName || r.siteAddress);

        // 사이트명/사이트주소는 DB상 NOT NULL 컬럼이므로,
        // 제출 대상 행 중 하나라도 누락되어 있으면 전체 등록을 막고 안내
        const hasInvalidRow = rows.some(r => !r.siteName || !r.siteAddress);

        if (rows.length === 0 || hasInvalidRow) {
            alert(t("사이트명_주소_입력", { ns: "admin" }));
            return;
        }

        onSubmit(rows);

        // 제출 후 입력창은 초기 상태(빈 행 1개)로 리셋
        setInputRows([{ id: Date.now(), siteName: "", siteAddress: "", faviconUrl: "" }]);
        setUrlErrors({});
        metaRequestSeq.current = {};
    };

    const handleClose = () => {
        setInputRows([{ id: Date.now(), siteName: "", siteAddress: "", faviconUrl: "" }]);
        setUrlErrors({});
        metaRequestSeq.current = {};
        onClose();
    };

    return (
        <div className={`form-border-box${isVisible ? "" : " hidden"}`}>
            <div className="form-row font-bold-title border-bottom-light">
                {t("URL_추가", { ns: "admin" })}
            </div>

            <div className="form-row align-top">
                <div style={{ flex: 1 }}>
                    {inputRows.map((row, idx) => (
                        <div className="url-input-row-group" key={row.id}>
                        {/* url-input-row: 한 행에 입력 필드를 가로 배치하고 오류는 아래에 표시 */}
                        <div className="dynamic-input-row url-input-row">

                            {/* 사이트명 (SITE_NAME, 필수) */}
                            <div className="input-wrapper url-input-name">
                                <input
                                    type="text"
                                    className="custom-input"
                                    placeholder={t("사이트명_힌트", { ns: "admin" })}
                                    value={row.siteName}
                                    onChange={(e) => handleChange(row.id, "siteName", e.target.value)}
                                />
                                <IconClear onClick={() => clearField(row.id, "siteName")} />
                            </div>

                            {/* 사이트주소 (SITE_ADD, 필수) */}
                            <div className="input-wrapper url-input-address">
                                <input
                                    type="text"
                                    className="custom-input"
                                    placeholder={t("사이트주소_힌트", { ns: "admin" })}
                                    value={row.siteAddress}
                                    onChange={(e) => handleChange(row.id, "siteAddress", e.target.value)}
                                    onBlur={(e) => handleAddressBlur(row.id, e.target.value)}
                                    onPaste={(e) => handleAddressPaste(row.id, e)}
                                />
                                {/* 메타 fetch 중이면 스피너, 아니면 지우기 아이콘 */}
                                {fetchingMeta[row.id]
                                    ? <span className="url-meta-loading" title="메타정보 가져오는 중...">⏳</span>
                                    : <IconClear onClick={() => clearField(row.id, "siteAddress")} />
                                }
                            </div>

                            {/* 파비콘 URL (FAVICON_URL, 선택) - 자동완성 후 수동 수정 가능 */}
                            {/* <div className="input-wrapper url-input-favicon">
                                <input
                                    type="text"
                                    className="custom-input"
                                    placeholder={t("파비콘_URL_힌트", { ns: "admin" })}
                                    value={row.faviconUrl}
                                    onChange={(e) => handleChange(row.id, "faviconUrl", e.target.value)}
                                />
                                <IconClear onClick={() => clearField(row.id, "faviconUrl")} />
                            </div> */}

                            {/* 첫 번째 행: 행 추가(+) / 이후 행: 행 삭제(-) - RegistrationForm과 동일한 패턴 */}
                            {idx === 0 ? (
                                <button className="btn-icon-action btn-add" onClick={addRow} title={t("추가", { ns: "common" })}>
                                    <IconPlus />
                                </button>
                            ) : (
                                <button className="btn-icon-action btn-remove" onClick={() => removeRow(row.id)} title={t("삭제", { ns: "common" })}>
                                    <IconMinus />
                                </button>
                            )}
                        </div>
                        {urlErrors[row.id] && (
                            <Alerts
                                kind={ConstData.ALERT_KIND.DANGER}
                                message={t(urlErrors[row.id], { ns: "admin" })}
                                style={{ width: "100%", margin: "6px 0 10px", padding: "7px 10px", fontSize: 12, lineHeight: 1.4, textAlign: "left" }}
                            />
                        )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="form-submit-row">
                <button className="btn-blue-submit" onClick={handleSubmit}>{t("추가", { ns: "common" })}</button>
            </div>
        </div>
    );
};

// ============================================================
// ManageTable
// Teacher / Class 공통 테이블
//
// 변경사항(이름 편집, 토글)은 localEdits에 임시 저장하고
// 저장 버튼 클릭 시에만 onSaveRow를 통해 API 호출
// ============================================================
const ManageTable = ({ items, currentPage, nameKey, onSaveRow, onDelete, rowsPerPage }) => {
    const { t } = useTranslation();

    const startIdx = (currentPage - 1) * rowsPerPage;
    const pageItems = items.slice(startIdx, startIdx + rowsPerPage);

    // localEdits: { [id]: { name?: string, isActive?: boolean } }
    // 사용자가 변경한 값을 임시 보관 — 저장 버튼 클릭 전까지 서버/Redux에 영향 없음
    const [localEdits, setLocalEdits] = useState({});

    // items(Redux props)가 바뀌면 localEdits 초기화
    // (저장 성공 후 서버 데이터가 내려오면 편집 잔여값 제거)
    useEffect(() => {
        setLocalEdits({});
    }, [items]);

    /** 화면에 표시할 값: localEdits 우선, 없으면 원본 props 값 */
    const getEditedItem = (item) => ({
        ...item,
        [nameKey]: localEdits[item.id]?.name ?? item[nameKey],
        isActive: localEdits[item.id]?.isActive ?? item.isActive,
    });

    /** 이름 인라인 확인 → localEdits에만 저장, API 호출 없음 */
    const handleLocalNameChange = (id, name) => {
        setLocalEdits(prev => ({
            ...prev,
            [id]: { ...prev[id], name }
        }));
    };

    /** 토글 클릭 → localEdits에만 저장, API 호출 없음 */
    const handleLocalToggle = (id, currentActive) => {
        setLocalEdits(prev => ({
            ...prev,
            [id]: { ...prev[id], isActive: !currentActive }
        }));
    };

    /** 저장 버튼 클릭 → localEdits가 반영된 값으로 API 호출 */
    const handleSave = (item) => {
        onSaveRow(getEditedItem(item));
    };

    return (
        <div className="table-container">
            <table className="list-table">
                <thead>
                    <tr>
                        <th style={{ width: '10%' }} className="text-center">{t("no.", { ns: "admin" })}</th>
                        <th style={{ width: '50%' }} className="text-center">
                            {nameKey === "teacherName" ? t("이름", { ns: "admin" }) : t("학급명", { ns: "admin" })}
                        </th>
                        <th style={{ width: '15%' }} className="text-center">{t("사용_여부", { ns: "admin" })}</th>
                        <th style={{ width: '25%' }} className="text-center">{t("관리", { ns: "admin" })}</th>
                    </tr>
                </thead>
                <tbody>
                    {pageItems.map((item, localIdx) => {
                        const globalIdx = startIdx + localIdx;
                        const editedItem = getEditedItem(item);
                        return (
                            <tr key={item.id}>
                                {/* 순번: 2자리 0 패딩 */}
                                <td className="text-center">
                                    {String(globalIdx + 1).padStart(2, "0")}
                                </td>

                                {/* 이름: 인라인 편집 확인 시 localEdits에만 반영 */}
                                <td className="text-center">
                                    <InlineEditCell
                                        item={editedItem}
                                        nameKey={nameKey}
                                        onSave={(id, name) => handleLocalNameChange(id, name)}
                                    />
                                </td>

                                {/* 토글: localEdits에만 반영 (API 호출 없음) */}
                                <td className="text-center">
                                    <ToggleSwitch
                                        checked={editedItem.isActive}
                                        onChange={() => handleLocalToggle(item.id, editedItem.isActive)}
                                    />
                                </td>

                                {/* 저장: localEdits(이름+사용여부)를 한 번에 API 전송
                                    삭제: 즉시 API 호출 */}
                                <td className="text-center">
                                    <button
                                        className="btn-table-action"
                                        title={t("저장", { ns: "common" })}
                                        onClick={() => handleSave(item)}
                                    >
                                        <IconSave />
                                    </button>
                                    <button
                                        className="btn-table-action btn-danger"
                                        title={t("삭제", { ns: "common" })}
                                        onClick={() => onDelete(item.id)}
                                    >
                                        <IconDelete />
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

// ============================================================
// UrlFieldCell
// URL 관리 테이블의 사이트명/사이트주소/파비콘URL 공용 인라인 편집 셀
// - InlineEditCell과 동일한 "클릭 → 입력 전환 → 확인/취소" 패턴을
//   필드 종류에 관계없이 재사용할 수 있도록 범용화한 버전
// - required=true 인 필드(사이트명/사이트주소)는 빈 값 확인 시 alert
// - showOpenLink=true 이면(사이트주소) 새 탭으로 열기 아이콘 노출
// ============================================================
const UrlFieldCell = ({ value, placeholder, required, requiredMessage, isActive, onSave, showOpenLink = false, onMetaFetched = null, onUrlError = null, onClearUrlError = null }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const [isFetching, setIsFetching] = useState(false);
    const metaRequestSeq = useRef(0);

    const handleStartEdit = () => {
        metaRequestSeq.current += 1;
        if (onClearUrlError) onClearUrlError();
        setEditValue(value);
        setIsEditing(true);
    };

    const handleConfirm = async () => {
        const trimmed = editValue.trim();
        if (required && !trimmed) {
            alert(requiredMessage);
            return;
        }

        // 260731 사이트주소 셀은 저장/메타조회 전에 공통 URL 검증 수행
        let valueToSave = trimmed;
        if (onMetaFetched) {
            const validation = normalizeAndValidateHttpUrl(trimmed);
            if (!validation.isValid) {
                if (onUrlError) onUrlError(validation.errorKey);
                return;
            }
            valueToSave = validation.url;
        }
        // 부모(UrlManageTable)의 localEdits에만 반영, API 호출은 저장 버튼에서 일괄 처리
        onSave(valueToSave);
        if (onClearUrlError) onClearUrlError();
        setIsEditing(false);

        // 2) onMetaFetched가 있는 셀(=사이트주소 셀)이면 메타 fetch
        if (onMetaFetched && valueToSave) {
            const requestSeq = metaRequestSeq.current + 1;
            metaRequestSeq.current = requestSeq;
            setIsFetching(true);
            try {
                const res = await get_url_meta({ url: valueToSave });
                if (metaRequestSeq.current !== requestSeq) return;
                const data = res.data;

                if (data.isSuccessed) {
                    // 부모가 현재 빈 필드만 채울지 여부를 판단하도록 raw 데이터 전달
                    onMetaFetched({ title: data.title, faviconUrl: data.faviconUrl });
                    if (onClearUrlError) onClearUrlError();
                } else if (onUrlError) {
                    onUrlError("URL_메타조회_실패");
                }
            } catch (err) {
                console.log("UrlFieldCell fetchUrlMeta err =>", err);
                if (metaRequestSeq.current === requestSeq && onUrlError) onUrlError("URL_메타조회_실패");
            } finally {
                if (metaRequestSeq.current === requestSeq) setIsFetching(false);
            }
        }
    };

    const handleCancel = () => {
        metaRequestSeq.current += 1;
        if (onClearUrlError) onClearUrlError();
        setEditValue(value);
        setIsEditing(false);
    };

    // 사이트주소를 새 탭으로 열기
    // - http(s):// 가 없으면 상대경로로 인식되는 것을 방지하기 위해 보정
    const handleOpenLink = () => {
        if (!value) return;
        const url = /^https?:\/\//i.test(value) ? value : `https://${value}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    if (isEditing) {
        return (
            <div className={`url-field-edit${showOpenLink ? " url-address-edit" : " url-name-edit"}`}>
                {/* 20260826 URL 관리 인라인 편집 입력창과 버튼 배치를 컬럼 폭에 맞춰 정돈한다. */}
                <input
                    type="text"
                    className="custom-input url-field-edit-input"
                    value={editValue}
                    placeholder={placeholder}
                    onChange={(e) => {
                        metaRequestSeq.current += 1;
                        setEditValue(e.target.value);
                        if (onClearUrlError) onClearUrlError();
                    }}
                    autoFocus
                />
                {/* <button className="btn-table-action" onClick={handleConfirm} style={{ marginLeft: 5 }}>확인</button> */}
                <button className='btn-table-action url-field-edit-button' onClick={handleConfirm}>
                    {isFetching ? "⏳" : "확인"}
                </button>
                <button className="btn-table-action" onClick={handleCancel}>취소</button>
            </div>
        );
    }

    return (
        <div className="url-field-view">
            <span className={`url-field-text${isActive ? " text-active" : ""}`} title={value}>
                {value || <span className="url-field-empty">{placeholder}</span>}
            </span>
            <IconPencil onClick={handleStartEdit} />
            {showOpenLink && !!value && <IconExternalLink onClick={handleOpenLink} />}
        </div>
    );
};

// ============================================================
// FaviconPreview
// 파비콘 URL을 작은 이미지로 미리보기
// - faviconUrl이 비어있거나 이미지 로드에 실패(깨진 링크/404 등)하면
//   IconLink로 대체 표시
// ============================================================
const FaviconPreview = ({ src }) => {
    const [hasError, setHasError] = useState(false);

    // src(파비콘 URL)가 바뀌면(=인라인 편집으로 값이 변경되면) 에러 상태를 초기화하여 재시도
    useEffect(() => {
        setHasError(false);
    }, [src]);

    if (!src || hasError) {
        return (
            <span className="favicon-fallback">
                <IconLink />
            </span>
        );
    }

    return (
        <img
            className="favicon-preview"
            src={src}
            alt=""
            onError={() => setHasError(true)}
        />
    );
};

// ============================================================
// UrlManageTable
// 고정 URL(CONTENT_URL_INFO) 목록 테이블
// - ManageTable과 동일하게 "localEdits에 임시 저장 → 저장 버튼 클릭 시
//   onSaveRow로 한 번에 API 전송"하는 구조를 따른다.
// - 차이점: 편집 가능한 필드가 3개(siteName, siteAddress, faviconUrl)이므로
//   localEdits의 형태가 { [id]: { siteName?, siteAddress?, faviconUrl?, isActive? } }
// ============================================================
const UrlManageTable = ({ items, currentPage, onSaveRow, onDelete, rowsPerPage }) => {
    const { t } = useTranslation();

    const startIdx = (currentPage - 1) * rowsPerPage;
    const pageItems = items.slice(startIdx, startIdx + rowsPerPage);

    // localEdits: { [id]: { siteName?, siteAddress?, faviconUrl?, isActive? } }
    const [localEdits, setLocalEdits] = useState({});
    // 260731 기존 URL 행별 검증/메타조회 오류 상태
    const [urlErrors, setUrlErrors] = useState({});

    // items(Redux props)가 갱신되면(=저장/삭제/재조회 등) localEdits 초기화
    useEffect(() => {
        setLocalEdits({});
        setUrlErrors({});
    }, [items]);

    useEffect(() => {
        setUrlErrors({});
    }, [currentPage]);

    const clearUrlError = (id) => {
        setUrlErrors(prev => {
            if (!prev[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    /** 화면에 표시할 값: localEdits 우선, 없으면 원본 props 값 */
    const getEditedItem = (item) => ({
        ...item,
        siteName: localEdits[item.id]?.siteName ?? item.siteName,
        siteAddress: localEdits[item.id]?.siteAddress ?? item.siteAddress,
        faviconUrl: localEdits[item.id]?.faviconUrl ?? item.faviconUrl,
        isActive: localEdits[item.id]?.isActive ?? item.isActive,
    });

    /** 특정 필드(사이트명/사이트주소/파비콘URL) 인라인 확인 → localEdits에만 저장 */
    const handleLocalFieldChange = (id, field, value) => {
        setLocalEdits(prev => ({
            ...prev,
            [id]: { ...prev[id], [field]: value }
        }));
    };

    /** 사용여부 토글 → localEdits에만 저장 */
    const handleLocalToggle = (id, currentActive) => {
        setLocalEdits(prev => ({
            ...prev,
            [id]: { ...prev[id], isActive: !currentActive }
        }));
    };

    /** 저장 버튼 클릭 → localEdits가 반영된 최종 값으로 API 호출 */
    const handleSave = (item) => {
        clearUrlError(item.id);
        onSaveRow(getEditedItem(item));
    };

    return (
        <div className="table-container">
            <table className="list-table">
                <thead>
                    <tr>
                        {/* <th style={{ width: '8%' }} className="text-center">{t("no.", { ns: "admin" })}</th>
                        <th style={{ width: '18%' }} className="text-center">{t("사이트명", { ns: "admin" })}</th>
                        <th style={{ width: '34%' }} className="text-center">{t("사이트주소", { ns: "admin" })}</th>
                        <th style={{ width: '18%' }} className="text-center">{t("파비콘_URL", { ns: "admin" })}</th>
                        <th style={{ width: '10%' }} className="text-center">{t("사용_여부", { ns: "admin" })}</th>
                        <th style={{ width: '12%' }} className="text-center">{t("관리", { ns: "admin" })}</th> */}
                        <th style={{ width: '8%' }} className="text-center">{t("no.", { ns: "admin" })}</th>
                        <th style={{ width: '22%' }} className="text-center">{t("사이트명", { ns: "admin" })}</th>
                        <th style={{ width: '46%' }} className="text-center">{t("사이트주소", { ns: "admin" })}</th>
                        <th style={{ width: '12%' }} className="text-center">{t("사용_여부", { ns: "admin" })}</th>
                        <th style={{ width: '12%' }} className="text-center">{t("관리", { ns: "admin" })}</th>
                    </tr>
                </thead>
                <tbody>
                    {pageItems.map((item, localIdx) => {
                        const globalIdx = startIdx + localIdx;
                        const editedItem = getEditedItem(item);
                        return (
                            <React.Fragment key={item.id}>
                            <tr>
                                {/* 순번: 2자리 0 패딩 (ManageTable과 동일) */}
                                <td className="text-center">
                                    {String(globalIdx + 1).padStart(2, "0")}
                                </td>

                                {/* 사이트명 (SITE_NAME, NOT NULL → required) */}
                                <td className="text-center">
                                    <UrlFieldCell
                                        value={editedItem.siteName}
                                        placeholder={t("사이트명_힌트", { ns: "admin" })}
                                        required
                                        requiredMessage={t("사이트명_입력", { ns: "admin" })}
                                        isActive={editedItem.isActive}
                                        onSave={(val) => handleLocalFieldChange(item.id, "siteName", val)}
                                    />
                                </td>

                                {/* 사이트주소 (SITE_ADD, NOT NULL → required) + 새 탭으로 열기 */}
                                <td className="text-center">
                                    <UrlFieldCell
                                        value={editedItem.siteAddress}
                                        placeholder={t("사이트주소_힌트", { ns: "admin" })}
                                        required
                                        requiredMessage={t("사이트주소_입력", { ns: "admin" })}
                                        isActive={editedItem.isActive}
                                        onSave={(val) => handleLocalFieldChange(item.id, "siteAddress", val)}
                                        showOpenLink
                                        onUrlError={(errorKey) => setUrlErrors(prev => ({ ...prev, [item.id]: errorKey }))}
                                        onClearUrlError={() => clearUrlError(item.id)}
                                        onMetaFetched={({ title, faviconUrl }) => {
                                            // 사이트명이 비어있을 때만 title로 채움
                                            if (!editedItem.siteName.trim()) {
                                                handleLocalFieldChange(item.id, "siteName", title ?? "");
                                            }
                                            // 파비콘URL이 비어있을 때만 채움
                                            if (!editedItem.faviconUrl.trim()) {
                                                handleLocalFieldChange(item.id, "faviconUrl", faviconUrl ?? "");
                                            }
                                        }}
                                    />
                                </td>

                                {/* 파비콘 URL (FAVICON_URL, NULL 허용 → required=false) + 미리보기 */}
                                {/* <td className="text-center">
                                    <div className="favicon-cell">
                                        <FaviconPreview src={editedItem.faviconUrl} />
                                        <UrlFieldCell
                                            value={editedItem.faviconUrl}
                                            placeholder={t("파비콘_URL_힌트", { ns: "admin" })}
                                            required={false}
                                            isActive={editedItem.isActive}
                                            onSave={(val) => handleLocalFieldChange(item.id, "faviconUrl", val)}
                                        />
                                    </div>
                                </td> */}

                                {/* 사용여부 토글: localEdits에만 반영 (API 호출 없음) */}
                                <td className="text-center">
                                    <ToggleSwitch
                                        checked={editedItem.isActive}
                                        onChange={() => handleLocalToggle(item.id, editedItem.isActive)}
                                    />
                                </td>

                                {/* 저장: localEdits(사이트명/사이트주소/파비콘URL/사용여부)를 한 번에 API 전송
                                    삭제: 즉시 API 호출 */}
                                <td className="text-center">
                                    <button
                                        className="btn-table-action"
                                        title={t("저장", { ns: "common" })}
                                        onClick={() => handleSave(item)}
                                    >
                                        <IconSave />
                                    </button>
                                    <button
                                        className="btn-table-action btn-danger"
                                        title={t("삭제", { ns: "common" })}
                                        onClick={() => {
                                            clearUrlError(item.id);
                                            onDelete(item.id);
                                        }}
                                    >
                                        <IconDelete />
                                    </button>
                                </td>
                            </tr>
                            {urlErrors[item.id] && (
                                <tr className="url-error-table-row">
                                    <td colSpan={5}>
                                        <Alerts
                                            kind={ConstData.ALERT_KIND.DANGER}
                                            message={t(urlErrors[item.id], { ns: "admin" })}
                                            style={{ width: "100%", margin: 0, padding: "7px 10px", fontSize: 12, lineHeight: 1.4, textAlign: "left" }}
                                        />
                                    </td>
                                </tr>
                            )}
                            </React.Fragment>
                        );
                    })}

                    {/* 등록된 URL이 없을 때 안내 행 */}
                    {pageItems.length === 0 && (
                        <tr>
                            <td colSpan={6} className="text-center" style={{ color: '#aaaaaa', padding: '24px 0' }}>
                                {t("등록된_URL_없음", { ns: "admin" })}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

// ============================================================
// WebTeacherClassConfig (메인 컴포넌트)
//
// Props (WebAdminContainer에서 전달):
//   list_contentTeacher  {Array}    - Redux admin.list_contentTeacher
//   isNeedTeacherRefresh {Boolean}  - Teacher 생성 후 목록 재조회 트리거 플래그
//   list_adminLive       {Array}    - Redux admin.list_adminLive (LIVE_INFO 테이블)
//   isNeedClassRefresh   {Boolean}  - Class 생성 후 목록 재조회 트리거 플래그
//   list_contentUrl      {Array}    - CONTENT_URL_INFO 목록 (Redux admin.list_contentUrl)
//   isNeedUrlRefresh     {Boolean}  - URL 일괄 등록 후 목록 재조회 트리거 플래그
//   handleSelectedFunc   {Function} - WebAdminContainer의 액션 위임 콜백
// ============================================================
const WebTeacherClassConfig = ({
    list_contentTeacher = [],
    isNeedTeacherRefresh,
    list_adminLive = [],
    isNeedClassRefresh,
    list_contentUrl = [],
    isNeedUrlRefresh,
    handleSelectedFunc
}) => {
    const { t } = useTranslation();

    // -------------------------------------------------------
    // 탭 상태: "teacher" | "class"
    // -------------------------------------------------------
    const [activeTab, setActiveTab] = useState("teacher");

    // -------------------------------------------------------
    // 페이지 번호
    // -------------------------------------------------------
    const [teacherPage, setTeacherPage] = useState(1);
    const [classPage, setClassPage] = useState(1);
    const [urlPage, setUrlPage] = useState(1);

    // -------------------------------------------------------
    // 페이지당 표시 개수 (teacher/class/url 3개 탭 공용, 10/20/30/50/100 선택 가능)
    // -------------------------------------------------------
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

    /** 페이지당 표시 개수 변경 시: 개수 변경 + 3개 탭 모두 1페이지로 리셋 */
    const handleChangeRowsPerPage = (newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setTeacherPage(1);
        setClassPage(1);
        setUrlPage(1);
    };

    // -------------------------------------------------------
    // 등록 폼 표시 여부
    // -------------------------------------------------------
    const [showTeacherForm, setShowTeacherForm] = useState(false);
    const [showClassForm, setShowClassForm] = useState(false);
    const [showUrlForm, setShowUrlForm] = useState(false);

    // ============================================================
    // 마운트 시 선생님 + 학급 + 고정 URL 목록 최초 로드
    // ============================================================
    useEffect(() => {
        handleSelectedFunc({ selectedFunc: "GET_ALL_CONTENT_TEACHERS" });
        handleSelectedFunc({ selectedFunc: "GET_ALL_CLASSES" });
        handleSelectedFunc({ selectedFunc: "GET_ALL_CONTENT_URLS" });
    }, []);

    // ============================================================
    // Teacher 생성(POST) 성공 후 isNeedTeacherRefresh 플래그가 켜지면 목록 재조회
    // → 서버에서 발급된 teacherSeq를 반영하기 위해 필요
    // ============================================================
    useEffect(() => {
        if (isNeedTeacherRefresh) {
            handleSelectedFunc({ selectedFunc: "GET_ALL_CONTENT_TEACHERS" });
        }
    }, [isNeedTeacherRefresh]);

    // ============================================================
    // Class 생성 성공 후 isNeedClassRefresh 플래그가 켜지면 목록 재조회
    // ============================================================
    useEffect(() => {
        if (isNeedClassRefresh) {
            handleSelectedFunc({ selectedFunc: "GET_ALL_CLASSES" });
        }
    }, [isNeedClassRefresh]);

    // ============================================================
    // URL 일괄 등록(POST) 성공 후 isNeedUrlRefresh 플래그가 켜지면 목록 재조회
    // → 서버에서 발급된 urlSeq를 반영하기 위해 필요 (Teacher 패턴과 동일)
    // ============================================================
    useEffect(() => {
        if (isNeedUrlRefresh) {
            handleSelectedFunc({ selectedFunc: "GET_ALL_CONTENT_URLS" });
        }
    }, [isNeedUrlRefresh]);

    // ============================================================
    // 선생님 탭 핸들러
    // ============================================================

    /** 등록 폼 토글 */
    const handleToggleTeacherForm = () => setShowTeacherForm(prev => !prev);

    /**
     * 선생님 일괄 추가
     * → create_content_teachers API (POST /admin/teacher)
     * → 성공 시 isNeedTeacherRefresh 플래그로 목록 재조회
     * @param {string[]} names - 입력된 이름 배열
     */
    const handleAddTeachers = (names) => {
        handleSelectedFunc({
            selectedFunc: "ADD_TEACHERS",
            data: names   // modules/admin.js에서 [{ teacherName }] 형태로 변환
        });
        setShowTeacherForm(false);

        // 추가 후 마지막 페이지로 이동 (재조회 완료 후 반영되므로 임시 계산)
        const estimatedTotal = list_contentTeacher.length + names.length;
        setTeacherPage(Math.ceil(estimatedTotal / rowsPerPage));
    };

    /**
     * 저장 버튼 클릭 (이름 + 사용여부 동시 저장)
     * → update_content_teacher_info API (PUT /admin/teacher/info/:seq)
     * ManageTable의 localEdits가 반영된 item을 받아 API 호출
     * @param {Object} item - localEdits 반영된 행 데이터 { id, teacherName, isActive }
     */
    const handleSaveTeacherRow = (item) => {
        handleSelectedFunc({
            selectedFunc: "SAVE_TEACHER_INFO",
            data: {
                teacherSeq: item.id,
                teacherName: item.teacherName,
                useYn: item.isActive ? "Y" : "N"
            }
        });
    };

    /**
     * 선생님 삭제
     * → delete_content_teacher API (DELETE /admin/teacher/:seq)
     * → Redux reducer에서 목록에서 즉시 제거
     * @param {number} id - 항목 ID (= teacherSeq)
     */
    const handleDeleteTeacher = (id) => {
        handleSelectedFunc({
            selectedFunc: "DELETE_TEACHER",
            data: { teacherSeq: id }
        });

        // 삭제 후 현재 페이지가 총 페이지 초과 시 마지막 페이지로 조정
        const remainCount = list_contentTeacher.length - 1;
        const maxPage = Math.max(1, Math.ceil(remainCount / rowsPerPage));
        if (teacherPage > maxPage) setTeacherPage(maxPage);
    };

    // ============================================================
    // Class 탭 핸들러 (Teacher와 동일한 API 연동 구조)
    // PUT /admin/live/:liveSeq 로 이름/사용여부 수정
    // DELETE 없이 LIVE_DEL_YN='Y' 처리 (delete_live)
    // ============================================================

    const handleToggleClassForm = () => setShowClassForm(prev => !prev);

    /**
     * 학급 일괄 추가
     * → Class 생성 API 없으므로 현재 미지원 (추후 백엔드 추가 시 연동)
     */
    const handleAddClasses = (names) => {
        // TODO: POST /admin/class 추가 후 아래 주석 해제
        // handleSelectedFunc({ selectedFunc: "ADD_CLASSES", data: names });
        console.warn("Class 생성 API 미구현 - 추후 연동 예정");
        setShowClassForm(false);
    };

    /**
     * 저장 버튼 클릭 (학급명 + 사용여부 동시 저장)
     * → update_live_info API (PUT /admin/live/:seq?kind=update)
     * ManageTable의 localEdits가 반영된 item을 받아 API 호출
     * @param {Object} item - localEdits 반영된 행 데이터 { id, liveName, isActive }
     */
    const handleSaveClassRow = (item) => {
        handleSelectedFunc({
            selectedFunc: "SAVE_CLASS_INFO",
            data: {
                liveSeq: item.id,
                liveName: item.liveName,
                useYn: item.isActive ? "Y" : "N"
            }
        });
    };

    /**
     * 학급 삭제
     * → delete_live API (PUT /admin/live/:seq?kind=delete, LIVE_DEL_YN='Y' 처리)
     * @param {number} id - 항목 ID (= liveSeq)
     */
    const handleDeleteClass = (id) => {
        handleSelectedFunc({
            selectedFunc: "DELETE_CLASS",
            data: { liveSeq: id }
        });

        // 삭제 후 현재 페이지가 총 페이지 초과 시 마지막 페이지로 조정
        const remainCount = list_adminLive.length - 1;
        const maxPage = Math.max(1, Math.ceil(remainCount / rowsPerPage));
        if (classPage > maxPage) setClassPage(maxPage);
    };

    // ============================================================
    // URL 탭 핸들러
    // - Teacher 탭(handleAddTeachers / handleSaveTeacherRow / handleDeleteTeacher)과
    //   동일한 흐름: 등록 폼 토글 → 일괄 추가 → 행별 저장/삭제
    // - 아래 selectedFunc("GET_ALL_CONTENT_URLS" 등)는 신규 액션이므로,
    //   modules/admin.js / WebAdminContainer.js / lib/api/admin.js 쪽에
    //   대응 thunk·API·reducer 구현이 필요합니다. (5번 섹션 참고)
    // ============================================================

    const handleToggleUrlForm = () => setShowUrlForm(prev => !prev);

    /**
     * 고정 URL 일괄 추가
     * → create_content_urls API (POST /admin/url) - 신규 구현 필요
     * → 성공 시 isNeedUrlRefresh 플래그로 목록 재조회
     * @param {Array<{siteName:string, siteAddress:string, faviconUrl:string}>} rows
     */
    const handleAddUrls = (rows) => {
        handleSelectedFunc({
            selectedFunc: "ADD_URLS",
            data: rows
        });
        setShowUrlForm(false);

        // 추가 후 마지막 페이지로 이동 (재조회 완료 후 반영되므로 임시 계산)
        const estimatedTotal = list_contentUrl.length + rows.length;
        setUrlPage(Math.ceil(estimatedTotal / rowsPerPage));
    };

    /**
     * 저장 버튼 클릭 (사이트명 + 사이트주소 + 파비콘URL + 사용여부 동시 저장)
     * → update_content_url_info API (PUT /admin/url/:seq) - 신규 구현 필요
     * UrlManageTable의 localEdits가 반영된 item을 받아 API 호출
     * @param {Object} item - { id, siteName, siteAddress, faviconUrl, isActive }
     */
    const handleSaveUrlRow = (item) => {
        handleSelectedFunc({
            selectedFunc: "SAVE_URL_INFO",
            data: {
                urlSeq: item.id,
                siteName: item.siteName,
                siteAddress: item.siteAddress,
                faviconUrl: item.siteAddress,
                // faviconUrl: item.faviconUrl,
                useYn: item.isActive ? "Y" : "N"
            }
        });
    };

    /**
     * 고정 URL 삭제
     * → delete_content_url API (DELETE /admin/url/:seq) - 신규 구현 필요
     * @param {number} id - 항목 ID (= urlSeq)
     */
    const handleDeleteUrl = (id) => {
        handleSelectedFunc({
            selectedFunc: "DELETE_URL",
            data: { urlSeq: id }
        });

        // 삭제 후 현재 페이지가 총 페이지 초과 시 마지막 페이지로 조정
        const remainCount = list_contentUrl.length - 1;
        const maxPage = Math.max(1, Math.ceil(remainCount / rowsPerPage));
        if (urlPage > maxPage) setUrlPage(maxPage);
    };

    // ============================================================
    // 렌더링
    // ============================================================

    const isTeacherTab = activeTab === "teacher";
    const isClassTab   = activeTab === "class";         // 기존 !isTeacherTab 대체
    const isUrlTab     = activeTab === "url";

    // Teacher: Redux props, Class: Redux props (LIVE_INFO 테이블)
    const teachers = list_contentTeacher;
    const classes = list_adminLive;
    const urls = list_contentUrl;

    return (
        <div style={{ /* backgroundColor: '#f5f6f8',  */ width: '100%', minHeight: '100vh', padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

            {/* ── 탭 버튼 ── */}
            <div className="page-nav-tabs">
                <button
                    className={`nav-tab${isTeacherTab ? " active" : ""}`}
                    onClick={() => setActiveTab("teacher")}
                >
                    {t("Teacher", { ns: "admin" })}
                </button>
                <button
                    className={`nav-tab${isClassTab ? " active" : ""}`}
                    onClick={() => setActiveTab("class")}
                >
                    {t("Class", { ns: "admin" })}
                </button>
                <button
                    className={`nav-tab${isUrlTab ? " active" : ""}`}
                    onClick={() => setActiveTab("url")}
                >
                    {t("URL_관리", { ns: "admin" })}
                </button>
            </div>

            <div className="app-container">

                {/* ── 타이틀 바 ── */}
                <div className="main-title-bar">
                    <div className="title-left">
                        {isTeacherTab && <IconTeacher />}
                        {isClassTab && <IconSchool />}
                        {isUrlTab && <IconLink className="icon-url" />}
                        <span className="title-text">
                            {isTeacherTab && t("선생님_등록", { ns: "admin" })}
                            {isClassTab && t("학급_등록", { ns: "admin" })}
                            {isUrlTab && t("고정_URL_관리", { ns: "admin" })}
                        </span>
                        {isTeacherTab &&
                            <button
                                className="btn-top-register"
                                onClick={handleToggleTeacherForm}
                            >
                                + {t("선생님_등록", { ns: "admin" })}
                            </button>
                        }
                        {/* isClassTab &&
                            <button
                                className="btn-top-register"
                                onClick={handleToggleClassForm}
                            >
                                + {t("학급_등록", { ns: "admin" })}
                            </button>
                         */}
                        {isUrlTab &&
                            <button className="btn-top-register" onClick={handleToggleUrlForm}>
                                + {t("URL_등록", { ns: "admin" })}
                            </button>
                        }
                    </div>
                </div>

                {/* ── 등록 폼 ── */}
                {isTeacherTab && (
                    <RegistrationForm
                        isVisible={showTeacherForm}
                        onSubmit={handleAddTeachers}
                        onClose={handleToggleTeacherForm}
                        mode="teacher"
                    />
                )}
                {isClassTab && (
                    <RegistrationForm
                        isVisible={showClassForm}
                        onSubmit={handleAddClasses}
                        onClose={handleToggleClassForm}
                        mode="class"
                    />
                )}
                {isUrlTab && (
                    <UrlRegistrationForm
                        isVisible={showUrlForm}
                        onSubmit={handleAddUrls}
                        onClose={handleToggleUrlForm}
                    />
                )}

                {/* ── 테이블 ── */}
                {isTeacherTab && (
                    <ManageTable
                        items={teachers}
                        currentPage={teacherPage}
                        nameKey="teacherName"
                        onSaveRow={handleSaveTeacherRow}
                        onDelete={handleDeleteTeacher}
                        rowsPerPage={rowsPerPage}
                    />
                )}
                {isClassTab && (
                    <ManageTable
                        items={classes}
                        currentPage={classPage}
                        nameKey="liveName"
                        onSaveRow={handleSaveClassRow}
                        onDelete={handleDeleteClass}
                        rowsPerPage={rowsPerPage}
                    />
                )}
                {isUrlTab && (
                    <UrlManageTable
                        items={urls}
                        currentPage={urlPage}
                        onSaveRow={handleSaveUrlRow}
                        onDelete={handleDeleteUrl}
                        rowsPerPage={rowsPerPage}
                    />
                )}

                {/* ── 페이지네이션 (페이지당 표시 개수 선택은 3개 탭 공용) ── */}
                {isTeacherTab && (
                    <Pagination total={teachers.length} currentPage={teacherPage} onChangePage={setTeacherPage} rowsPerPage={rowsPerPage} onChangeRowsPerPage={handleChangeRowsPerPage} />
                )}
                {isClassTab && (
                    <Pagination total={classes.length} currentPage={classPage} onChangePage={setClassPage} rowsPerPage={rowsPerPage} onChangeRowsPerPage={handleChangeRowsPerPage} />
                )}
                {isUrlTab && (
                    <Pagination total={urls.length} currentPage={urlPage} onChangePage={setUrlPage} rowsPerPage={rowsPerPage} onChangeRowsPerPage={handleChangeRowsPerPage} />
                )}
            </div>
        </div>
    );
};

export default WebTeacherClassConfig;
